/**
 * 비회원 작성 초안 보관 — 광고 유입 → 체험 → 가입 전환용.
 *
 * 왜 localStorage 인가: 가입 도중 카카오 로그인 등으로 페이지가 완전히 이동하므로
 * sessionStorage 로는 살아남지 못한다. 대신 오래 남지 않도록 만료(7일)를 둔다.
 *
 * ⚠️ 개인정보(이름·연락처·주소 등)는 담지 않는다. 공용 PC 에 그대로 남는다.
 *    이름/연락처는 가입 시 계정 정보로 채우면 되고, 초안에는 '무엇을 만들려 했는지'만 담는다.
 * ⚠️ 파일(File 객체)은 직렬화되지 않는다. 사진은 업로드 완료된 URL 만 저장할 것.
 *
 *   saveDraft("quote", { categoryId, answers })
 *   const d = loadDraft("quote")   // 없거나 만료면 null
 *   clearDraft("quote")            // 제출 성공 후 반드시 호출
 */

const PREFIX = "muyeon.draft.";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7일

const keyOf = (name) => `${PREFIX}${name}`;

// 로그인 회원 초안 이름: 같은 브라우저를 쓰는 다른 계정에 남의 초안이 붙지 않게 회원 번호로 나눈다.
//  비회원은 이름 그대로(가입 전 초안 → 가입 후 복원 흐름이 같은 이름을 쓴다).
const USER_PREFIX_RE = /^u\d+:/;
export const userDraftName = (userId, name) => `u${userId}:${name}`;

export function saveDraft(name, data, savedAt = Date.now()) {
  if (!name) return false;
  try {
    localStorage.setItem(keyOf(name), JSON.stringify({ savedAt, data }));
    return true;
  } catch (e) {
    return false; // 용량 초과·시크릿 모드 등
  }
}

export function loadDraft(name) {
  return loadDraftEntry(name)?.data ?? null;
}

/** 저장 시각까지 함께 꺼낸다: { savedAt, data } (없거나 만료면 null). */
export function loadDraftEntry(name) {
  if (!name) return null;
  try {
    const raw = localStorage.getItem(keyOf(name));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // 만료된 초안이 뒤늦게 되살아나 혼선 주는 것 방지
    if (!parsed.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      clearDraft(name);
      return null;
    }
    if (parsed.data == null) return null;
    return { savedAt: parsed.savedAt, data: parsed.data };
  } catch (e) {
    return null;
  }
}

export function hasDraft(name) {
  return loadDraft(name) !== null;
}

export function clearDraft(name) {
  try { localStorage.removeItem(keyOf(name)); } catch (e) { /* noop */ }
}

const removeDraftKeys = (match) => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX) && match(k.slice(PREFIX.length)))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* noop */ }
};
const clearFunnelRedirect = () => {
  // 인증 대기 공고 복귀 경로(util/pendingPosting.js FUNNEL_KEY)도 함께 지운다. 초안 없이 남으면
  //  다음 사람이 인증 서류를 낼 때 엉뚱한 등록 화면으로 튕긴다.
  try { sessionStorage.removeItem("muyeon.funnel.redirect"); } catch (e) { /* noop */ }
};

/** 한 회원의 초안(muyeon.draft.u<id>:*)만 지운다. 로그아웃에서 쓴다. */
export function clearUserDrafts(userId) {
  if (userId == null || userId === "") return;
  const prefix = userDraftName(userId, "");
  removeDraftKeys((name) => name.startsWith(prefix));
}

/** 회원 번호가 붙지 않은 초안(비회원 초안·예전 방식 초안)만 지운다. */
export function clearGuestDrafts() {
  removeDraftKeys((name) => !USER_PREFIX_RE.test(name));
  clearFunnelRedirect();
}

/** 초안 일괄 정리(모든 회원·비회원). */
export function clearAllDrafts() {
  removeDraftKeys(() => true);
  clearFunnelRedirect();
}

// 가입·로그인 직후 초안 유지 표시 — 비회원 작성 화면의 [가입하고 계속하기]/[로그인]에서만 남긴다.
//  유효시간을 둬서, 가입을 포기하고 자리를 뜬 뒤 같은 브라우저에서 다른 사람이 로그인해도 초안이 붙지 않게 한다.
const PRESERVE_KEY = "muyeon.preserveDraftOnLogin";
const PRESERVE_TTL_MS = 30 * 60 * 1000; // 30분
//  ★ 10-24 localStorage 로 — 인스타 앱 안 브라우저에서 카카오톡을 거쳐 돌아오면 새 탭으로 열릴 때가 있어
//    sessionStorage 표시가 사라지고 초안까지 지워졌다. 30분 만료는 그대로라 공용 기기 보호는 같다.

export function markPreserveDraftOnLogin(name) {
  try { localStorage.setItem(PRESERVE_KEY, JSON.stringify({ name: name || "", at: Date.now() })); } catch (e) { /* noop */ }
}

/** 초안 유지 표시를 꺼내고 지운다. 유효시간 안에 남긴 표시면 true. */
export function consumePreserveDraftOnLogin() {
  try {
    const raw = localStorage.getItem(PRESERVE_KEY) ?? sessionStorage.getItem(PRESERVE_KEY);
    localStorage.removeItem(PRESERVE_KEY);
    sessionStorage.removeItem(PRESERVE_KEY);
    if (!raw) return false;
    const v = JSON.parse(raw);
    return !!v && typeof v.at === "number" && Date.now() - v.at <= PRESERVE_TTL_MS;
  } catch (e) {
    return false; // 형식이 다른 옛 표시는 유지하지 않는다
  }
}
